import type { CalculationMemoryBreakdownRow } from "@bi/types";

/**
 * Lógica das abas da memória de cálculo ("Por que variou", "Quem compõe",
 * documentos). Tudo é feito na tela, a partir do que a API de memória de
 * cálculo já devolve: o mesmo endpoint chamado para dois períodos e a
 * composição por cliente/fornecedor. Nenhuma regra de cálculo do número muda.
 */

export type MemoryTab = "calculo" | "variacao" | "composicao" | "documentos";
export type Comparison = "anterior" | "ano";

// ── períodos ─────────────────────────────────────────────────────────────────

/** Datas do filtro são instantes UTC da meia-noite de São Paulo (UTC−3), com fim exclusivo. */
const SP_OFFSET_MS = 3 * 3600_000;

type Ymd = { y: number; m: number; d: number }; // m = 0..11

function toYmd(iso: string): Ymd {
  const t = new Date(new Date(iso).getTime() - SP_OFFSET_MS);
  return { y: t.getUTCFullYear(), m: t.getUTCMonth(), d: t.getUTCDate() };
}

function fromYmd({ y, m, d }: Ymd): string {
  return new Date(Date.UTC(y, m, d) + SP_OFFSET_MS).toISOString();
}

function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
}

function shiftMonths(p: Ymd, months: number): Ymd {
  const total = p.y * 12 + p.m + months;
  const y = Math.floor(total / 12);
  const m = total - y * 12;
  return { y, m, d: Math.min(p.d, daysInMonth(y, m)) };
}

/**
 * Período de comparação.
 * - "ano": o mesmo período do ano anterior (29/02 vira 28/02).
 * - "anterior": se o período começa no dia 1 (mês atual, mês fechado,
 *   trimestre…), o mesmo recorte nos meses imediatamente anteriores — "1 a 16
 *   de outubro" compara com "1 a 16 de setembro", como no resto do BI. Fora
 *   disso (hoje, últimos 7 dias, datas livres), o mesmo número de dias logo antes.
 */
export function comparisonPeriod(from: string, to: string, kind: Comparison): { from: string; to: string } {
  const a = toYmd(from);
  const b = toYmd(to);
  if (kind === "ano") return { from: fromYmd(shiftMonths(a, -12)), to: fromYmd(shiftMonths(b, -12)) };
  if (a.d === 1) {
    // meses cobertos: do mês de início até o mês do último dia incluído
    const last = toYmd(new Date(new Date(to).getTime() - 86_400_000).toISOString());
    const months = (last.y * 12 + last.m) - (a.y * 12 + a.m) + 1;
    return { from: fromYmd(shiftMonths(a, -months)), to: fromYmd(shiftMonths(b, -months)) };
  }
  const len = new Date(to).getTime() - new Date(from).getTime();
  return { from: new Date(new Date(from).getTime() - len).toISOString(), to: from };
}

/** "1 a 16 de out. de 2026" — período com fim exclusivo. */
export function periodLabel(from: string, to: string): string {
  const MESES = ["jan.", "fev.", "mar.", "abr.", "maio", "jun.", "jul.", "ago.", "set.", "out.", "nov.", "dez."];
  const a = toYmd(from);
  const b = toYmd(new Date(new Date(to).getTime() - 86_400_000).toISOString());
  if (a.y === b.y && a.m === b.m && a.d === b.d) return `${a.d} de ${MESES[a.m]} de ${a.y}`;
  if (a.y === b.y && a.m === b.m) return `${a.d} a ${b.d} de ${MESES[a.m]} de ${a.y}`;
  if (a.y === b.y) return `${a.d} de ${MESES[a.m]} a ${b.d} de ${MESES[b.m]} de ${b.y}`;
  return `${a.d} de ${MESES[a.m]} de ${a.y} a ${b.d} de ${MESES[b.m]} de ${b.y}`;
}

// ── por que variou ───────────────────────────────────────────────────────────

export interface Contribution {
  key: string;
  label: string;
  before: number;
  now: number;
  delta: number;
}

export interface VariationBridge {
  before: number;
  now: number;
  /** grupos presentes nos dois períodos */
  grew: number;
  shrank: number;
  /** só no período atual / só no de comparação */
  added: number;
  lost: number;
  addedCount: number;
  lostCount: number;
  /**
   * Diferença entre o total oficial (ERP ao vivo) e a soma da composição
   * (armazém sincronizado). Fica à parte para a ponte fechar com os totais da tela.
   */
  syncGap: number;
  /** quantidade × valor médio (só quando há quantidade de documentos nos dois lados) */
  countBefore: number;
  countNow: number;
  volumeEffect: number | null;
  averageEffect: number | null;
  risers: Contribution[];
  fallers: Contribution[];
}

export function buildVariation(input: {
  before: { total: number; count: number; rows: CalculationMemoryBreakdownRow[] };
  now: { total: number; count: number; rows: CalculationMemoryBreakdownRow[] };
  top?: number;
}): VariationBridge {
  const top = input.top ?? 8;
  const map = new Map<string, Contribution>();
  for (const r of input.before.rows) {
    map.set(r.groupKey, { key: r.groupKey, label: r.groupLabel, before: r.total, now: 0, delta: 0 });
  }
  for (const r of input.now.rows) {
    const c = map.get(r.groupKey);
    if (c) { c.now = r.total; c.label = r.groupLabel; }
    else map.set(r.groupKey, { key: r.groupKey, label: r.groupLabel, before: 0, now: r.total, delta: 0 });
  }

  let grew = 0, shrank = 0, added = 0, lost = 0, addedCount = 0, lostCount = 0;
  let sumBefore = 0, sumNow = 0;
  const all: Contribution[] = [];
  for (const c of map.values()) {
    c.delta = c.now - c.before;
    sumBefore += c.before;
    sumNow += c.now;
    if (c.before !== 0 && c.now !== 0) {
      if (c.delta > 0) grew += c.delta; else shrank += c.delta;
    } else if (c.now !== 0) { added += c.now; addedCount++; }
    else if (c.before !== 0) { lost -= c.before; lostCount++; }
    if (Math.abs(c.delta) >= 0.005) all.push(c);
  }

  const before = input.before.total;
  const now = input.now.total;
  const syncGap = (now - before) - (sumNow - sumBefore);

  const n0 = input.before.count;
  const n1 = input.now.count;
  let volumeEffect: number | null = null;
  let averageEffect: number | null = null;
  if (n0 > 0 && n1 > 0) {
    const m0 = before / n0;
    const m1 = now / n1;
    volumeEffect = (n1 - n0) * m0;
    averageEffect = (m1 - m0) * n1;
  }

  const risers = all.filter((c) => c.delta > 0).sort((a, b) => b.delta - a.delta).slice(0, top);
  const fallers = all.filter((c) => c.delta < 0).sort((a, b) => a.delta - b.delta).slice(0, top);

  return {
    before, now, grew, shrank, added, lost, addedCount, lostCount, syncGap,
    countBefore: n0, countNow: n1, volumeEffect, averageEffect, risers, fallers,
  };
}

// ── quem compõe (curva ABC) ──────────────────────────────────────────────────

export type AbcClass = "A" | "B" | "C";

export interface AbcRow extends CalculationMemoryBreakdownRow {
  rank: number;
  share: number;      // % do total positivo
  cumulative: number; // % acumulado até esta linha, inclusive
  abc: AbcClass;
}

/**
 * Classe A: o que vem antes dela ainda não chegou a 80% do total; B: antes de
 * 95%; o resto é C. Valores zerados ou negativos ficam em C.
 */
export function classifyAbc(rows: CalculationMemoryBreakdownRow[], limitA = 80, limitB = 95): AbcRow[] {
  const sorted = [...rows].sort((a, b) => b.total - a.total);
  const positive = sorted.reduce((s, r) => s + Math.max(0, r.total), 0) || 1;
  let acc = 0;
  return sorted.map((r, i) => {
    const share = (Math.max(0, r.total) / positive) * 100;
    const before = acc;
    acc += share;
    const abc: AbcClass = r.total <= 0 ? "C" : before < limitA ? "A" : before < limitB ? "B" : "C";
    return { ...r, rank: i + 1, share, cumulative: Math.min(100, acc), abc };
  });
}

export function abcSummary(rows: AbcRow[]) {
  const out = { A: { count: 0, value: 0 }, B: { count: 0, value: 0 }, C: { count: 0, value: 0 } };
  for (const r of rows) { out[r.abc].count++; out[r.abc].value += r.total; }
  return out;
}

// ── documentos ───────────────────────────────────────────────────────────────

/** Nome da aba de documentos conforme o que cada número soma (a Transmasi não tem NF-e de venda). */
export function documentsTabLabel(cardKey: string): string {
  if (cardKey.startsWith("cte-")) return "CT-es";
  if (cardKey === "recebido-no-periodo") return "Recebimentos";
  if (cardKey === "pago-no-periodo") return "Pagamentos";
  if (cardKey === "dre-frete-motorista") return "Cartas-frete";
  if (cardKey === "saldo-bancario") return "Contas";
  if (cardKey === "fluxo-projetado-30d") return "Lançamentos";
  return "Títulos";
}

/** Quantidade de documentos no singular/plural, para frases ("888 CT-es"). */
export function countNoun(cardKey: string, n: number): string {
  const one = n === 1;
  if (cardKey.startsWith("cte-")) return one ? "CT-e" : "CT-es";
  if (cardKey === "recebido-no-periodo") return one ? "recebimento" : "recebimentos";
  if (cardKey === "pago-no-periodo") return one ? "pagamento" : "pagamentos";
  if (cardKey === "dre-frete-motorista") return one ? "carta-frete" : "cartas-frete";
  if (cardKey === "saldo-bancario") return one ? "conta" : "contas";
  return one ? "título" : "títulos";
}

/** CSV no padrão do Excel brasileiro: `;` como separador, vírgula decimal, BOM. */
export function toCsv(header: string[], rows: Array<Array<string | number | null>>): string {
  const cell = (v: string | number | null) => {
    if (v == null) return "";
    const s = typeof v === "number" ? String(v).replace(".", ",") : v;
    return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return "﻿" + [header, ...rows].map((r) => r.map(cell).join(";")).join("\r\n");
}

/** Como chamar o agrupamento da composição. */
export function groupNouns(groupBy: string): { one: string; many: string; title: string } {
  switch (groupBy) {
    case "supplier": return { one: "fornecedor", many: "fornecedores", title: "Fornecedor" };
    case "bank_account": return { one: "conta", many: "contas", title: "Conta bancária" };
    case "period": return { one: "período", many: "períodos", title: "Período" };
    case "driver": return { one: "motorista", many: "motoristas", title: "Motorista" };
    default: return { one: "cliente", many: "clientes", title: "Cliente" };
  }
}

/** Números em que subir é ruim para a empresa (custos e impostos). */
export function upIsBad(cardKey: string): boolean {
  return ["pago-no-periodo", "dre-frete-motorista", "cte-icms-mes", "a-pagar-em-aberto", "pagamentos-atrasados", "inadimplencia-total", "inadimplencia-recente-30d"].includes(cardKey);
}
